import { intersectionBy, unionBy } from "lodash-es";
import type { FC } from "react";
import { useDispatch } from "react-redux";
import type { FilterOptionOption } from "react-select/dist/declarations/src/filters.js";
import { type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { Dialog } from "widgets";
import { useControlledDialog } from "widgets/hooks";

import { simpleDropdownFieldValidation } from "@/app-utils/form.js";
import type { InputModifyProjectFileInput, Organism, ProjectFile } from "@/graphql.js";
import { IndexStatus } from "@/graphql.js";
import { isIndexRequiredForGenomeViewer } from "@/modules/files/utils/file.js";
import { sendFilesToGenomeBrowser, updateProjectFile } from "@/redux/modules/files.js";
import { searchSequencedGenomes } from "@/redux/modules/genome-browser.js";
import type { ReduxDispatch } from "@/redux/redux-utils.js";

import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";

interface IBamFileValidationDialogProps {
  projectFiles: ProjectFile[];
  onFileAssociationsUpdated?: () => void;
  onClose: () => void;
}

const getGenomesIntersection = (files: ProjectFile[]) => {
  return intersectionBy(...files.map((file) => file.organisms), "id");
};

const getGenomesUnion = (files: ProjectFile[]) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore // it seems that the corresponding typescript definition is missing
  return unionBy(...files.map((file) => file.organisms), "id");
};

export const getGenomeViewerResourceData = (files: ProjectFile[]) => {
  const genomesIntersection = getGenomesIntersection(files);
  const genomesIntersectionOfAllFilesHavingAttachedOrganisms = getGenomesIntersection(
    files.filter((file) => file.organisms?.length),
  );

  // scenario 1: some files do not have necessary resources
  const missingResources: ProjectFile[] = files.filter(
    (file) => isIndexRequiredForGenomeViewer(file) && file.indexingStatus !== IndexStatus.Exists,
  );

  // scenario 2: multiple files (not necessarily all) have attached genome(s) but they don't intersect
  const noIntersectingGenomes: boolean =
    !genomesIntersection.length &&
    !genomesIntersectionOfAllFilesHavingAttachedOrganisms.length &&
    files.filter((file) => file.organisms?.length)?.length > 1;

  // scenario 3: all files have one and the same ref genome association
  const uniqueCommonGenome: boolean =
    !missingResources.length && !noIntersectingGenomes && genomesIntersection.length === 1;

  // scenario 4: the intersection has more than one genome, the user has to pick one
  const pickFromIntersection: boolean =
    !missingResources.length && genomesIntersectionOfAllFilesHavingAttachedOrganisms.length > 1;

  // scenario 5: none of the files have an attached genome
  const noGenomes: boolean = !missingResources.length && files.every((file) => !file.organisms?.length);

  // scenario 6: some files have no attached genome, and some others do have one single intersecting
  const uniqueGenomeToBeConfirmed: boolean =
    !missingResources.length &&
    !genomesIntersection.length &&
    genomesIntersectionOfAllFilesHavingAttachedOrganisms.length === 1;

  return {
    missingResources,
    noIntersectingGenomes,
    uniqueCommonGenome,
    pickFromIntersection,
    noGenomes,
    uniqueGenomeToBeConfirmed,
    genomesIntersectionOfAllFilesHavingAttachedOrganisms,
  };
};

const BamFileValidationDialog: FC<IBamFileValidationDialogProps & InjectedFormProps<any, any>> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const { dialogProps, dialogRef, underlayProps } = useControlledDialog(props.onClose);

  const {
    uniqueCommonGenome,
    missingResources,
    noIntersectingGenomes,
    pickFromIntersection,
    noGenomes,
    uniqueGenomeToBeConfirmed,
    genomesIntersectionOfAllFilesHavingAttachedOrganisms,
  } = getGenomeViewerResourceData(props.projectFiles);
  const genomesUnion = getGenomesUnion(props.projectFiles);

  // the case of "uniqueCommonGenome" should not be tackled here but rather directly in files.sendFilesToGenomeBrowser
  // because it does not need any validation dialog ?
  if (uniqueCommonGenome) {
    sendFilesToGenomeBrowser(props.projectFiles, genomesIntersectionOfAllFilesHavingAttachedOrganisms?.[0]?.id);
    props.onClose();
  }

  const submitRefGenome = (values) => {
    const pickedGenome: Organism = uniqueGenomeToBeConfirmed
      ? genomesIntersectionOfAllFilesHavingAttachedOrganisms[0]
      : values[BamFileValidationGenomeField];

    // update the associations
    return Promise.all(
      props.projectFiles
        // get all files where this genome association is not registered
        .filter((projectFile) => {
          return !projectFile.organisms.find((genome: Organism) => genome.id === pickedGenome.id);
        })
        // remove all files that are protected and not deletable, meaning we should not update the genome association
        .filter((projectFile) => !projectFile.isProtected && projectFile.isEditable)
        // update the files using the file modification dedicated endpoint with the new ref genome attachment
        .map((projectFile) => {
          const initialGenomes = projectFile.organisms?.map((genome) => genome.id) || [];
          const fileInput: InputModifyProjectFileInput = {
            id: projectFile.id,
            name: projectFile.fileName,
            description: projectFile.description,
            url: projectFile.url,
            organismIds: initialGenomes.concat([pickedGenome.id]),
            isProtected: projectFile.isProtected,
          };
          return dispatch(updateProjectFile(fileInput));
        }),
    )
      .then(() => {
        props.onFileAssociationsUpdated?.();
        sendFilesToGenomeBrowser(props.projectFiles, pickedGenome.id);
        props.onClose();
      })
      .catch((error) => {
        throw new SubmissionError({ _error: error?.message || error });
      });
  };

  return (
    <Dialog
      ref={dialogRef}
      domProps={dialogProps}
      underlayProps={underlayProps}
      title={missingResources.length ? "Missing Resources" : "Reference Genome Association"}
      mainButton={
        !!missingResources.length || noIntersectingGenomes
          ? undefined
          : { label: "Open Genome Viewer", onPress: props.handleSubmit(submitRefGenome) }
      }
      closeFn={props.onClose}
      errorCallout={props.error ? { message: props.error } : undefined}
      width={580}
    >
      {pickFromIntersection && (
        <>
          <p>
            The files you selected are associated with multiple reference genomes. <br />
            To view the data, please choose a reference genome.
          </p>
          <ReactSelectReduxFormField
            name={BamFileValidationGenomeField}
            horizontalLayout={false}
            label={`${props.projectFiles.map((projectFile) => projectFile.fileName).join(", ")}`}
            placeholder={`Please select a reference genome...`}
            component={ReactSelectField}
            isClearable={false}
            isSearchable={true}
            options={genomesIntersectionOfAllFilesHavingAttachedOrganisms}
            validate={simpleDropdownFieldValidation}
            formatOptionLabel={(genome: Organism) => genome.name}
            filterOption={(option: FilterOptionOption<Organism>, inputValue) =>
              option.data.name.toLowerCase().includes(inputValue.toLowerCase()) ||
              option.data.identifier.toLowerCase().includes(inputValue.toLowerCase())
            }
          />
        </>
      )}

      {noGenomes && (
        <>
          <p>
            The files you selected have not been associated with a reference genome. <br />
            To view the data, please choose a reference genome.
          </p>
          <ReactSelectReduxFormField
            name={BamFileValidationGenomeField}
            horizontalLayout={false}
            label={`${props.projectFiles.map((projectFile) => projectFile.fileName).join(", ")}`}
            placeholder={`Please select a reference genome...`}
            component={ReactSelectField}
            isClearable={false}
            isSearchable={true}
            validate={simpleDropdownFieldValidation}
            formatOptionLabel={(genome: Organism) => genome.name}
            async={true}
            loadOptions={(searchTerm: string) =>
              dispatch(searchSequencedGenomes(searchTerm)).then((search) => search.genomes)
            }
          />
        </>
      )}

      {!!missingResources.length && (
        <>
          <p>The following files do not have the necessary resources to be processed in the Genome Browser:</p>
          <p>
            {missingResources.map((projectFile) => (
              <>
                {projectFile.fileName} <i>({getMissingResourcesMessage(projectFile)})</i>
                <br />
              </>
            ))}
          </p>
        </>
      )}

      {noIntersectingGenomes && (
        <>
          <p>
            To view NGS read data relative to a reference genome, each of the files in your selection must be associated
            with a common reference genome. <br />
            The following reference genomes are present in your selection.
          </p>
          <p>
            {genomesUnion.map((genome) => (
              <>
                {genome.name}
                <br />
              </>
            ))}
          </p>
        </>
      )}

      {uniqueGenomeToBeConfirmed && (
        <>
          <p>
            Some of the files you selected have not been associated with a reference genome. <br />
            If you continue, all of the selected files will be associated with the following reference genome:
          </p>
          <p>
            {genomesIntersectionOfAllFilesHavingAttachedOrganisms.map((genome) => (
              <>
                {genome.name}
                <br />
              </>
            ))}
          </p>
        </>
      )}
    </Dialog>
  );
};

function getMissingResourcesMessage(projectFile: ProjectFile) {
  if (projectFile.indexingStatus) {
    if (isIndexRequiredForGenomeViewer(projectFile) && projectFile.indexingStatus !== IndexStatus.Exists) {
      return "Index and/or coverage file are missing";
    }
  }
}

const BamFileValidationForm = "BamFileValidationForm";
const BamFileValidationGenomeField = "BamFileValidationGenomeField";

export default reduxForm<{}, IBamFileValidationDialogProps>({
  form: BamFileValidationForm,
  //validate: ConfigureParentTypeAttributeDialogImpl.validate,
})(BamFileValidationDialog as any);
